import React, { useState, useEffect, useCallback } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { PlusIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { utcToDateInput, dateInputToUTC } from '../utils/datetime';

const DISCOUNT_TYPES = [
  { value: 'percentage', label: 'Percentage off' },
  { value: 'fixed', label: 'Fixed amount off' },
  { value: 'free_ride', label: 'Free ride (up to cap)' },
];

const EMPTY_FORM = {
  code: '',
  description: '',
  discountType: 'percentage',
  discountValue: 10,
  maxDiscount: '',
  minFare: '',
  allowedRideTypes: [],
  firstRideOnly: false,
  validFrom: '',
  validUntil: '',
  maxUses: 0,
  perUserLimit: 1,
  isActive: true,
};


export default function Coupons() {
  const [coupons, setCoupons] = useState([]);
  const [rideTypeOptions, setRideTypeOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // existing coupon being edited
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const panelOpen = creating || !!selected;

  const fetchCoupons = useCallback(async () => {
    try {
      const res = await api.get('/admin/coupons');
      setCoupons(res.data?.data || []);
    } catch {
      toast.error('Failed to load coupons');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRideTypes = useCallback(async () => {
    try {
      const res = await api.get('/admin/ride-types');
      const types = res.data?.data || [];
      setRideTypeOptions(types.map((t) => ({ value: t.key, label: t.label })));
    } catch { /* non-critical */ }
  }, []);

  useEffect(() => {
    fetchCoupons();
    fetchRideTypes();
  }, [fetchCoupons, fetchRideTypes]);

  const openCreate = () => {
    setSelected(null);
    setCreating(true);
    setForm(EMPTY_FORM);
  };

  const openEdit = (coupon) => {
    setCreating(false);
    setSelected(coupon);
    setForm({
      code: coupon.code,
      description: coupon.description || '',
      discountType: coupon.discountType,
      discountValue: coupon.discountValue ?? 0,
      maxDiscount: coupon.maxDiscount ?? '',
      minFare: coupon.minFare ?? '',
      allowedRideTypes: (coupon.allowedRideTypes || []).map((k) => {
        const opt = rideTypeOptions.find((o) => o.value === k);
        return opt || { value: k, label: k };
      }),
      firstRideOnly: !!coupon.firstRideOnly,
      validFrom:  utcToDateInput(coupon.validFrom),
      validUntil: utcToDateInput(coupon.validUntil),
      maxUses: coupon.maxUses ?? 0,
      perUserLimit: coupon.perUserLimit ?? 1,
      isActive: coupon.isActive,
    });
  };

  const closePanel = () => {
    setSelected(null);
    setCreating(false);
    setForm(EMPTY_FORM);
  };

  const buildPayload = () => ({
    code: form.code.trim().toUpperCase(),
    description: form.description.trim(),
    discountType: form.discountType,
    discountValue: Number(form.discountValue) || 0,
    maxDiscount: form.maxDiscount === '' ? null : Number(form.maxDiscount),
    minFare: form.minFare === '' ? 0 : Number(form.minFare),
    allowedRideTypes: form.allowedRideTypes.map((o) => o.value),
    firstRideOnly: form.firstRideOnly,
    validFrom:  dateInputToUTC(form.validFrom,  'start'),
    validUntil: dateInputToUTC(form.validUntil, 'end'),
    maxUses: Number(form.maxUses) || 0,
    perUserLimit: Number(form.perUserLimit) || 0,
    isActive: form.isActive,
  });

  const handleSave = async () => {
    if (!form.code.trim()) { toast.error('Coupon code is required'); return; }
    if (form.discountType !== 'free_ride' && Number(form.discountValue) <= 0) {
      toast.error('Discount value must be greater than 0'); return;
    }
    setSaving(true);
    try {
      if (selected) {
        await api.put(`/admin/coupons/${selected._id}`, buildPayload());
        toast.success('Coupon updated');
      } else {
        await api.post('/admin/coupons', buildPayload());
        toast.success('Coupon created');
      }
      closePanel();
      fetchCoupons();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to save coupon');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (!selected) return;
    setDeleteTarget(selected);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/coupons/${deleteTarget._id}`);
      toast.success('Coupon deleted');
      setDeleteTarget(null);
      closePanel();
      fetchCoupons();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to delete coupon');
    } finally {
      setDeleting(false);
    }
  };

  const describeDiscount = (c) => {
    if (c.discountType === 'percentage') {
      return `${c.discountValue}% off${c.maxDiscount ? ` (max ${c.maxDiscount})` : ''}`;
    }
    if (c.discountType === 'fixed') return `${c.discountValue} off`;
    return `Free ride${c.maxDiscount ? ` (up to ${c.maxDiscount})` : ''}`;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Coupons</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Discount codes riders apply at checkout.
          </p>
        </div>
        <button onClick={openCreate} className="btn-primary text-sm flex items-center gap-1.5">
          <PlusIcon className="w-4 h-4" /> New Coupon
        </button>
      </div>

      <div className="flex gap-4 items-start">
        {/* List */}
        <div className="flex-1 card overflow-hidden p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
            </div>
          ) : coupons.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              No coupons yet. Create your first promo code.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Code</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Discount</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Used</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {coupons.map((c) => (
                  <tr
                    key={c._id}
                    className={`hover:bg-gray-50 cursor-pointer transition-colors ${selected?._id === c._id ? 'bg-indigo-50' : ''}`}
                    onClick={() => openEdit(c)}
                  >
                    <td className="px-4 py-3 font-mono font-medium text-gray-800">{c.code}</td>
                    <td className="px-4 py-3 text-gray-600">{describeDiscount(c)}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {c.usedCount || 0}{c.maxUses ? ` / ${c.maxUses}` : ''}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`status-badge ${c.isActive ? 'status-active' : 'status-inactive'}`}>
                        {c.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Editor panel */}
        {panelOpen && (
          <div className="card w-96 flex-shrink-0 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-800 flex items-center gap-1.5">
                {selected
                  ? <><PencilIcon className="w-4 h-4" /> Edit Coupon</>
                  : <><PlusIcon className="w-4 h-4" /> New Coupon</>}
              </h3>
              <button onClick={closePanel} className="text-gray-400 hover:text-gray-600 text-lg leading-none">✕</button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code <span className="text-red-500">*</span></label>
                <input
                  className="input text-sm font-mono uppercase"
                  placeholder="WELCOME20"
                  value={form.code}
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  className="input text-sm"
                  placeholder="20% off your ride"
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    className="input text-sm"
                    value={form.discountType}
                    onChange={(e) => setForm((f) => ({ ...f, discountType: e.target.value }))}
                  >
                    {DISCOUNT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                {form.discountType !== 'free_ride' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {form.discountType === 'percentage' ? 'Percent (%)' : 'Amount (QAR)'}
                    </label>
                    <input
                      type="number" min={0} className="input text-sm"
                      value={form.discountValue}
                      onChange={(e) => setForm((f) => ({ ...f, discountValue: e.target.value }))}
                    />
                  </div>
                )}
              </div>

              {(form.discountType === 'percentage' || form.discountType === 'free_ride') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max discount (QAR){form.discountType === 'free_ride' ? ' — fare cap' : ''}
                  </label>
                  <input
                    type="number" min={0} className="input text-sm" placeholder="No cap"
                    value={form.maxDiscount}
                    onChange={(e) => setForm((f) => ({ ...f, maxDiscount: e.target.value }))}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Min fare to qualify (QAR)</label>
                <input
                  type="number" min={0} className="input text-sm" placeholder="0"
                  value={form.minFare}
                  onChange={(e) => setForm((f) => ({ ...f, minFare: e.target.value }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Allowed ride types</label>
                <Select
                  isMulti
                  options={rideTypeOptions}
                  value={form.allowedRideTypes}
                  onChange={(v) => setForm((f) => ({ ...f, allowedRideTypes: v || [] }))}
                  placeholder="All types"
                  classNamePrefix="react-select"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valid from</label>
                  <input type="date" className="input text-sm" value={form.validFrom}
                    onChange={(e) => setForm((f) => ({ ...f, validFrom: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valid until</label>
                  <input type="date" className="input text-sm" value={form.validUntil}
                    onChange={(e) => setForm((f) => ({ ...f, validUntil: e.target.value }))} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max uses (0 = ∞)</label>
                  <input type="number" min={0} className="input text-sm" value={form.maxUses}
                    onChange={(e) => setForm((f) => ({ ...f, maxUses: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Per-user limit</label>
                  <input type="number" min={0} className="input text-sm" value={form.perUserLimit}
                    onChange={(e) => setForm((f) => ({ ...f, perUserLimit: e.target.value }))} />
                </div>
              </div>

              <label className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">First ride only</span>
                <input type="checkbox" checked={form.firstRideOnly}
                  onChange={(e) => setForm((f) => ({ ...f, firstRideOnly: e.target.checked }))} />
              </label>

              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">Active</label>
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, isActive: !f.isActive }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.isActive ? 'bg-indigo-600' : 'bg-gray-300'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.isActive ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              {selected && (
                <button type="button" onClick={handleDelete} disabled={deleting || saving}
                  className="btn-danger text-sm flex items-center gap-1.5">
                  <TrashIcon className="w-4 h-4" /> {deleting ? 'Deleting…' : 'Delete'}
                </button>
              )}
              <button type="button" onClick={handleSave} disabled={saving || deleting}
                className="btn-primary text-sm flex-1">
                {saving ? 'Saving…' : selected ? 'Save Changes' : 'Create Coupon'}
              </button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Delete Coupon"
        message={`Delete coupon "${deleteTarget?.code}"? This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        loading={deleting}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
